import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { get, pick } from 'lodash';
import querystring from 'querystring';
import { FontFamily } from '@zocdoc/components/src/typography';
import { Textbox } from '@zocdoc/components/src/textbox';
import { Button } from '@zocdoc/components/src/button';
import { Link } from '@zocdoc/components/src/link';
import { determineTarget } from '@zocdoc/components/src/link/Link';
import { coral, yellow, navy, white } from '@zocdoc/branding/colors';
import { breakpoint, Breakpoints, getWindowWidth } from '@zocdoc/components/src/grid';
import { MagnifyingGlass } from '@zocdoc/components/src/icons';
import { LOCALIZED_STRINGS } from './constants';
import { SpecialtyIds } from '../../../../constants';
import fetchCurrentLocation from '../../../../Search/utils/fetch-current-location';

class BaseInviteDoctorForm extends PureComponent {
    static propTypes = {
        emailValid: PropTypes.bool.isRequired,
        firstNameValid: PropTypes.bool.isRequired,
        isMessageSent: PropTypes.bool.isRequired,
        lastNameValid: PropTypes.bool.isRequired,
        onBlurDoctorsEmail: PropTypes.func.isRequired,
        onBlurFirstName: PropTypes.func.isRequired,
        onBlurLastName: PropTypes.func.isRequired,
        onClickClaimThisProfile: PropTypes.func.isRequired,
        onClickFindAnotherDoctor: PropTypes.func.isRequired,
        onClickSend: PropTypes.func.isRequired,
        professionalName: PropTypes.string.isRequired,
        shouldShowNewSidebar: PropTypes.bool.isRequired,
        showFindSimilarDoctors: PropTypes.bool.isRequired,
        specialty: PropTypes.object.isRequired,
        submitError: PropTypes.bool.isRequired,
    };

    render() {
        const {
            shouldShowNewSidebar,
            showFindSimilarDoctors,
        } = this.props;

        if (shouldShowNewSidebar) {
            return showFindSimilarDoctors
                ? this.renderFindSimilarDoctorsForm()
                : this.renderNewSidebar();
        } else {
            return this.renderNormalForm();
        }
    }

    renderFindSimilarDoctorsForm() {
        const {
            onClickClaimThisProfile,
            provider,
            specialty,
        } = this.props;

        const specialtyName = specialty.name.toLowerCase();

        return (
            <StyledContainer>
                <CantBookMessage>
                    <FormattedMessage
                        {...LOCALIZED_STRINGS.NotAvailable}
                        values={{ specialtyName }}
                    />
                </CantBookMessage>
                <FindSimilarDoctorsButton
                    href={buildSimilarDoctorsSearchUrl(provider)}
                    onClick={this.handleClickFindSimilarDoctors}
                >
                    <StyledMagnifyingGlass
                        color="currentcolor"
                        height="20"
                    />
                    <FormattedMessage {...LOCALIZED_STRINGS.FindSimilarDoctors} />
                </FindSimilarDoctorsButton>
                <ClaimProfileMessage>
                    <FormattedMessage
                        {...LOCALIZED_STRINGS.AreYouDoctor}
                        values={{ nameInSentence: provider.nameInSentence }}
                    />
                    <ClaimLinkBold
                        data-test="claim-link"
                        href={this.getJoinLink()}
                        onClick={onClickClaimThisProfile}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.ClaimProfile} />
                    </ClaimLinkBold>
                </ClaimProfileMessage>
            </StyledContainer>
        );
    }

    renderNewSidebar() {
        const {
            onClickClaimThisProfile,
            professionalName,
            specialty,
        } = this.props;

        const specialtyName = specialty.name.toLowerCase();

        const nameInSentence = (() => {
            const dr = professionalName.substring(0, professionalName.indexOf('.') + 1);
            const nameWithoutEnd = professionalName.substring(professionalName.indexOf(dr), professionalName.indexOf(','));
            const firstName = nameWithoutEnd.substring(0, nameWithoutEnd.lastIndexOf(' '));
            const lastName = nameWithoutEnd.substring(nameWithoutEnd.lastIndexOf(' '), nameWithoutEnd.length);
            const end = professionalName.substring(professionalName.indexOf(','), professionalName.length);

            const prefix = dr || firstName;

            return `${prefix}${lastName}${end}`;
        })();

        return (
            <div data-test="find-available-container">
                <Card>
                    <CardHeader>
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.NotAvailable}
                            values={{ specialtyName }}
                        />
                    </CardHeader>
                </Card>
                <Card>
                    <CardParagraph>
                        <AreYouDoctorText>
                            <FormattedMessage
                                {...LOCALIZED_STRINGS.AreYouDoctor}
                                values={{ nameInSentence }}
                            />
                        </AreYouDoctorText>
                        <ClaimLink
                            data-test="claim-link"
                            href={this.getJoinLink()}
                            onClick={onClickClaimThisProfile}
                        >
                            <FormattedMessage {...LOCALIZED_STRINGS.ClaimProfile} />
                        </ClaimLink>
                    </CardParagraph>
                </Card>
            </div>
        );
    }

    renderNormalForm() {
        const {
            intl,
            isMessageSent,
            professionalName,
            emailValid,
            firstNameValid,
            lastNameValid,
            onClickSend,
        } = this.props;
        if (!isMessageSent) {
            return (
                <Container data-test="invite-doctor-form">
                    <div data-test="invite-doctor-form-description">
                        <FormTextParagraph>
                            <WantToBookMessage>
                                <FormattedMessage
                                    {...LOCALIZED_STRINGS.WantToBookWith}
                                    values={{ professionalName }}
                                />
                            </WantToBookMessage>
                            &nbsp;
                            <FormattedMessage
                                {...LOCALIZED_STRINGS.CompleteTheForm}
                                values={{ professionalName }}
                            />
                        </FormTextParagraph>
                    </div>
                    <FormFieldContainer>
                        <FormInputHeader data-test="doctors-email">
                            <FormattedMessage {...LOCALIZED_STRINGS.DoctorsEmail} />
                        </FormInputHeader>
                        <Textbox
                            data-test="doctors-email-input"
                            invalidMessage={emailValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterAValidEmail)}
                            onBlur={this.onBlurDoctorsEmail}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.EmailAddressPlaceholder)}
                        />
                    </FormFieldContainer>
                    <FormFieldContainer>
                        <FormInputHeader data-test="first-name">
                            <FormattedMessage {...LOCALIZED_STRINGS.YourFirstName} />
                        </FormInputHeader>
                        <Textbox
                            data-test="first-name-input"
                            invalidMessage={firstNameValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterAFirstName)}
                            onBlur={this.onBlurFirstName}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.FirstNamePlaceholder)}
                        />
                    </FormFieldContainer>
                    <FormFieldContainer>
                        <FormInputHeader data-test="last-name">
                            <FormattedMessage {...LOCALIZED_STRINGS.YourLastName} />
                        </FormInputHeader>
                        <Textbox
                            data-test="last-name-input"
                            invalidMessage={lastNameValid
                                ? undefined
                                : intl.formatMessage(LOCALIZED_STRINGS.PleaseEnterALastName)}
                            onBlur={this.onBlurLastName}
                            placeholder={intl.formatMessage(LOCALIZED_STRINGS.LastNamePlaceholder)}
                        />
                    </FormFieldContainer>
                    <FullWidthButton
                        data-test="send-button"
                        onClick={onClickSend}
                        style={Button.STYLE.TRANSPARENT}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.Send} />
                    </FullWidthButton>
                    {this.maybeRenderSubmitError()}
                </Container>
            );
        } else {
            return (
                <Container data-test="message-sent-form">
                    <FormTextParagraph data-test="message-sent">
                        <FormattedMessage {...LOCALIZED_STRINGS.MessageSent} />
                    </FormTextParagraph>
                    <FormTextParagraphNoMargin data-test="message-sent-description">
                        <FormattedMessage
                            {...LOCALIZED_STRINGS.MessageSentDescription}
                            values={{ professionalName }}
                        />
                    </FormTextParagraphNoMargin>
                    <FullWidthButton
                        data-test="find-another-doctor-button"
                        onClick={this.handleClickFindAnotherDoctor}
                        style={Button.STYLE.TRANSPARENT}
                    >
                        <FormattedMessage {...LOCALIZED_STRINGS.FindAnotherDoctor} />
                    </FullWidthButton>
                </Container>
            );
        }
    }

    maybeRenderSubmitError = () => {
        if (this.props.submitError) {
            return (
                <FormInputError data-test="submit-error">
                    <FormattedMessage {...LOCALIZED_STRINGS.SomethingWentWrong} />
                </FormInputError>
            );
        }
    };

    getJoinLink() {
        const {
            provider,
        } = this.props;

        const wacogapactStates = new Set(['wa', 'co', 'ga', 'pa', 'ct']);

        const redirectToNewAboutPage = provider.locations.some(x => wacogapactStates.has(x.state.toLowerCase()));
        return redirectToNewAboutPage ? '/about/join' : '/join';
    }

    onBlurDoctorsEmail = e => this.props.onBlurDoctorsEmail(e.currentTarget.value);
    onBlurFirstName = e => { this.props.onBlurFirstName(e.currentTarget.value); };
    onBlurLastName = e => { this.props.onBlurLastName(e.currentTarget.value); };

    handleClickFindAnotherDoctor = async () => {
        this.props.onClickFindAnotherDoctor();
        window.location.assign('/search');
    };

    handleClickFindSimilarDoctors = async event => {
        event.preventDefault();
        this.props.onClickFindSimilarDoctors(event);

        let coordinates;

        if (getWindowWidth() < Breakpoints.small) {
            // If on mobile, try to get the user's location.
            // Otherwise the location will use a reverse-ip lookup.
            try {
                const currentLocation = await fetchCurrentLocation();
                coordinates = pick(currentLocation, ['longitude', 'latitude']);
            } catch (error) { /* do nothing */ }
        }

        window.open(
            buildSimilarDoctorsSearchUrl(this.props.provider, coordinates),
            determineTarget(event),
        );
    }
}

function buildSimilarDoctorsSearchUrl(provider, additionalParams) {
    const specialtyId = get(provider, 'relevantSpecialty.id', SpecialtyIds.PRIMARY_CARE_PHYSICIAN);

    const query = querystring.stringify({
        dr_specialty: specialtyId,
        referrerType: 'PreviewProfile',
        ...additionalParams,
    });

    return `/search?${query}`;
}

const Container = styled.div`
    padding: 30px;

    ${breakpoint.small} {
        padding: 30px 60px;
    }

    ${breakpoint.medium} {
        padding: 12px 0 16px 0;
    }
`;

const FormFieldContainer = styled.div`
    margin-top: 16px;
`;

const FormTextParagraph = styled.p`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
    margin-bottom: 24px;
    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.SharpSansSemibold};
    }
`;

const FormTextParagraphNoMargin = styled(FormTextParagraph)`
    margin-bottom: 0;
`;

const WantToBookMessage = styled.span`
    font-family: ${FontFamily.SharpSansBold};
`;

const FormInputHeader = styled.h3`
    font: 15px/24px ${FontFamily.SharpSansBold};
    margin-bottom: 4px;
    ${breakpoint.medium} {
        font: 18px/28px ${FontFamily.SharpSansBold};
    }
`;

const FormInputError = styled.p`
    margin-top: 4px;
    font: 15/24px ${FontFamily.SharpSansMedium};
    color: ${coral};
    ${breakpoint.medium} {
        font: 18/28px ${FontFamily.SharpSansMedium};
    }
`;

const FullWidthButton = styled(Button)`
    width: 100%;
    margin-top: 30px;
`;

const StyledContainer = styled.div`
    padding: 28px 20px 26px;

    ${breakpoint.medium} {
        padding: 20px 0;
    }
`;

const CantBookMessage = styled.p`
    font: 15px/24px ${FontFamily.SharpSansSemibold};

    ${breakpoint.medium} {
        font-size: 18px;
    }
`;

const FindSimilarDoctorsButton = styled.a`
    background-color: ${yellow};
    display: block;
    font-family: ${FontFamily.SharpSansSemibold};
    font-size: 18px;
    line-height: 28px;
    margin-top: 16px;
    padding: 10px 8px;
    text-align: center;

    span {
        vertical-align: middle;
    }

    :hover {
        background-color: ${navy};
        color: ${white};
    }
`;

const ClaimProfileMessage = styled.p`
    ${breakpoint('small', { down: true })} {
        margin-top: 26px;
        font-size: 13px;
    }

    ${breakpoint.medium} {
        margin-top: 22px;
    }
`;

const StyledMagnifyingGlass = styled(MagnifyingGlass)`
    margin-right: 7px;
    vertical-align: middle;
`;

const ClaimLinkBold = styled(Link)`
    margin-left: 4px;
    font-family: ${FontFamily.SharpSansSemibold};
`;

const Card = styled.div`
    margin-top: 24px;
    background-color: white;
    padding: 20px 20px 20px 24px;

    ${breakpoint('medium', { down: true })} {
        margin-top: 10px;
    }
`;

const CardHeader = styled.p`
    font: 18px/24px ${FontFamily.SharpSansSemibold};
`;

const CardParagraph = styled.p`
    font: 18px/24px ${FontFamily.SharpSansMedium};
`;

const ClaimLink = styled(Link)`
    padding: 0 0 0 4px;
`;

const AreYouDoctorText = styled.span`
    font-family: ${FontFamily.SharpSansSemibold};
`;

export const InviteDoctorForm = injectIntl(BaseInviteDoctorForm);
